import React, { useState } from 'react';
import { FolderGit2, ExternalLink, Eye } from 'lucide-react';
import { portfolioData } from '../data/portfolioData';
import { Project } from '../types/portfolio';
import { ProjectModal } from './ProjectModal';

export const Projects: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const categories = ['All', 'Enterprise', 'Full Stack', 'Mobile', 'AI & Cloud'];

  const filteredProjects =
    activeCategory === 'All'
      ? portfolioData.projects
      : portfolioData.projects.filter((p) => p.category === activeCategory);

  return (
    <section id="work" className="section projects-section">
      <div className="container">
        <div className="section-header">
          <div className="section-badge">
            <FolderGit2 size={14} />
            <span>Portfolio Showcase</span>
          </div>
          <h2 className="section-title">
            Featured <span className="text-gradient">Projects & Work</span>
          </h2>
          <p className="section-subtitle">
            A selection of mission-critical web applications, enterprise systems, and scalable platforms engineered for industry leaders.
          </p>
        </div>

        {/* Filter Categories */}
        <div className="project-categories">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`cat-btn ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="projects-grid">
          {filteredProjects.map((project) => (
            <div key={project.id} className="project-card glass-card">
              <div className="project-thumb">
                <img
                  src={project.image}
                  alt={project.name}
                  className="thumb-img"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/assets/images/projects/work-dummy.png';
                  }}
                />
                <div className="thumb-overlay">
                  <button
                    className="overlay-btn"
                    onClick={() => setSelectedProject(project)}
                    title="Quick Details"
                  >
                    <Eye size={18} />
                    <span>View Case Study</span>
                  </button>

                  {project.link && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="overlay-btn outline-btn"
                      title="Visit Site"
                    >
                      <ExternalLink size={18} />
                    </a>
                  )}
                </div>
                <div className="project-category-tag">{project.category}</div>
              </div>

              <div className="project-content">
                <h3 className="project-name">{project.name}</h3>
                <p className="project-desc">{project.description}</p>

                <div className="project-footer">
                  <div className="mini-tags">
                    {project.technologies.slice(0, 3).map((tech) => (
                      <span key={tech} className="mini-tag">
                        {tech}
                      </span>
                    ))}
                    {project.technologies.length > 3 && (
                      <span className="mini-tag extra-tag">
                        +{project.technologies.length - 3}
                      </span>
                    )}
                  </div>

                  <button
                    className="details-link"
                    onClick={() => setSelectedProject(project)}
                  >
                    View Details &rarr;
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal Popup */}
      <ProjectModal
        project={selectedProject}
        allProjects={portfolioData.projects}
        onClose={() => setSelectedProject(null)}
        onSelectProject={(project) => setSelectedProject(project)}
      />

      <style>{`
        .projects-section {
          background: var(--bg-primary);
        }

        .project-categories {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          margin-bottom: 3rem;
          flex-wrap: wrap;
        }

        .cat-btn {
          padding: 0.6rem 1.35rem;
          border-radius: var(--radius-full);
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          color: var(--text-muted);
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.25s ease;
        }

        .cat-btn:hover {
          color: var(--text-main);
          border-color: var(--border-highlight);
        }

        .cat-btn.active {
          background: var(--gradient-brand);
          color: #ffffff;
          border-color: transparent;
          box-shadow: var(--shadow-glow);
        }

        .projects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 2rem;
        }

        .project-card {
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .project-thumb {
          position: relative;
          width: 100%;
          height: 220px;
          overflow: hidden;
          background: var(--bg-tertiary);
        }

        .thumb-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: top left;
          transition: transform 0.5s ease;
        }

        .project-card:hover .thumb-img {
          transform: scale(1.04);
        }

        .thumb-overlay {
          position: absolute;
          inset: 0;
          background: rgba(9, 13, 22, 0.75);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .project-card:hover .thumb-overlay {
          opacity: 1;
        }

        .overlay-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 1.2rem;
          border-radius: var(--radius-full);
          background: var(--gradient-brand);
          color: #fff;
          font-size: 0.88rem;
          font-weight: 600;
          border: none;
          cursor: pointer;
          text-decoration: none;
          transition: transform 0.2s ease;
        }

        .overlay-btn:hover {
          transform: scale(1.05);
        }

        .outline-btn {
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          padding: 0.6rem;
        }

        .project-category-tag {
          position: absolute;
          top: 1rem;
          left: 1rem;
          padding: 0.25rem 0.75rem;
          border-radius: var(--radius-full);
          background: var(--bg-glass);
          backdrop-filter: blur(8px);
          border: 1px solid var(--border-color);
          color: var(--accent-secondary);
          font-size: 0.78rem;
          font-weight: 700;
        }

        .project-content {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          flex-grow: 1;
        }

        .project-name {
          font-size: 1.2rem;
          font-weight: 700;
          margin-bottom: 0.75rem;
          color: var(--text-main);
          line-height: 1.35;
        }

        .project-desc {
          font-size: 0.9rem;
          color: var(--text-muted);
          line-height: 1.6;
          margin-bottom: 1.5rem;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .project-footer {
          margin-top: auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          padding-top: 1rem;
          border-top: 1px solid var(--border-color);
        }

        .mini-tags {
          display: flex;
          gap: 0.4rem;
          flex-wrap: wrap;
        }

        .mini-tag {
          padding: 0.2rem 0.6rem;
          border-radius: var(--radius-sm);
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          color: var(--text-muted);
          font-size: 0.75rem;
          font-weight: 600;
        }

        .extra-tag {
          color: var(--accent-primary);
          border-color: var(--border-highlight);
        }

        .details-link {
          background: none;
          border: none;
          color: var(--accent-primary);
          font-size: 0.85rem;
          font-weight: 700;
          cursor: pointer;
          transition: color 0.2s ease;
          padding: 0;
          white-space: nowrap;
        }

        .details-link:hover {
          color: var(--accent-secondary);
        }

        @media (max-width: 768px) {
          .projects-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
};
